/*
Código hecho por Rcrea
=========================================================
RCREA PRODUCT SHOWCASE
Archivo: assets/rcrea-product-showcase.js
- Paginación calculada por cards reales
- Gap uniforme
- Compatible con mobile full width
- Swipe nativo
- Cards manuales / colección / mixto
=========================================================
*/
(() => {
  const selectors = {
    carousel: "[data-rps-carousel]",
    viewport: "[data-rps-viewport]",
    track: "[data-rps-track]",
    prev: "[data-rps-prev]",
    next: "[data-rps-next]",
    card: "[data-rps-card]",
    addButton: "[data-rps-add-to-cart]"
  };
  const CART_SECTION_IDS = ["mini-cart", "header"];
  const CART_DRAWER_SELECTOR = "cart-drawer, #CartDrawer, .cart-drawer, [data-cart-drawer]";
  const QUICK_ADD_SELECTOR = [
    "quick-add-modal",
    "quick-buy-drawer",
    "product-quick-add",
    "product-modal",
    ".quick-add-modal",
    ".quick-buy-drawer",
    ".product-popup-modal",
    "[id*='QuickAdd']",
    "[id*='quick-add']",
    "[data-quick-add-modal]",
    "[data-quick-buy]"
  ].join(",");
  const routesRoot = () => {
    if (window.Shopify?.routes?.root) return window.Shopify.routes.root;
    return "/";
  };
  const normalizeRoot = () => {
    const root = routesRoot();
    return root.endsWith("/") ? root : `${root}/`;
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, "text/html");
  class RcreaProductShowcaseCarousel {
    constructor(carousel) {
      this.carousel = carousel;
      this.viewport = carousel.querySelector(selectors.viewport);
      this.track = carousel.querySelector(selectors.track);
      this.prevButton = carousel.querySelector(selectors.prev);
      this.nextButton = carousel.querySelector(selectors.next);
      this.resizeTimer = null;
      if (!this.viewport || !this.track) return;
      this.bindEvents();
      window.requestAnimationFrame(() => this.updateControls());
    }
    bindEvents() {
      this.viewport.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleResize);
      this.prevButton?.addEventListener("click", this.handlePrevClick);
      this.nextButton?.addEventListener("click", this.handleNextClick);
    }
    unbindEvents() {
      this.viewport.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleResize);
      this.prevButton?.removeEventListener("click", this.handlePrevClick);
      this.nextButton?.removeEventListener("click", this.handleNextClick);
    }
    handleResize = () => {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.updateControls();
      }, 150);
    };
    handleScroll = () => {
      this.updateControls();
    };
    handlePrevClick = (event) => {
      event.preventDefault();
      this.scrollByPage(-1);
    };
    handleNextClick = (event) => {
      event.preventDefault();
      this.scrollByPage(1);
    };
    getItems() {
      return Array.from(this.track.querySelectorAll(":scope > [data-rps-card]"));
    }
    getColumns() {
      const styles = window.getComputedStyle(this.track);
      const value = parseInt(styles.getPropertyValue("--rps-product-columns"), 10);
      return Number.isNaN(value) || value < 1 ? 1 : value;
    }
    getGap() {
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap);
      return Number.isNaN(gap) ? 0 : gap;
    }
    hasOverflow() {
      return this.getItems().length > this.getColumns();
    }
    getPageStep() {
      const items = this.getItems();
      if (!items.length) return 0;
      const columns = this.getColumns();
      const cardWidth = items[0].getBoundingClientRect().width;
      const gap = this.getGap();
      return (cardWidth + gap) * columns;
    }
    getCurrentPage() {
      const step = this.getPageStep();
      if (step <= 0) return 0;
      return Math.round(this.viewport.scrollLeft / step);
    }
    getMaxPage() {
      const items = this.getItems();
      const columns = this.getColumns();
      if (!items.length || !columns) return 0;
      return Math.max(0, Math.ceil(items.length / columns) - 1);
    }
    scrollByPage(direction) {
      if (!this.hasOverflow()) return;
      const currentPage = this.getCurrentPage();
      const maxPage = this.getMaxPage();
      const targetPage = Math.max(0, Math.min(maxPage, currentPage + direction));
      const items = this.getItems();
      const columns = this.getColumns();
      const targetIndex = Math.min(targetPage * columns, items.length - 1);
      const targetItem = items[targetIndex];
      if (!targetItem) return;
      const viewportStyles = window.getComputedStyle(this.viewport);
      const paddingLeft = parseFloat(viewportStyles.paddingLeft) || 0;
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const targetLeft = Math.max(0, Math.min(targetItem.offsetLeft - paddingLeft, maxScroll));
      this.viewport.scrollTo({
        left: targetLeft,
        behavior: "smooth"
      });
    }
    updateControls() {
      const overflow = this.hasOverflow();
      if (this.prevButton) this.prevButton.hidden = !overflow;
      if (this.nextButton) this.nextButton.hidden = !overflow;
    }
    destroy() {
      this.unbindEvents();
      window.clearTimeout(this.resizeTimer);
    }
  }
  const initCarousels = () => {
    document.querySelectorAll(selectors.carousel).forEach((carousel) => {
      if (carousel.dataset.rpsCarouselInitialized === "true") return;
      carousel.dataset.rpsCarouselInitialized = "true";
      carousel.rcreaProductShowcaseCarousel = new RcreaProductShowcaseCarousel(carousel);
    });
  };
  const isCartDrawerElement = (element) => {
    if (!element || element.nodeType !== 1) return false;
    if (element.matches?.(CART_DRAWER_SELECTOR)) return true;
    if (element.querySelector?.(CART_DRAWER_SELECTOR)) return true;
    return false;
  };
  const ownTitleText = (element) => {
    const title = element?.querySelector?.(".quick-add-modal__title, .modal__title, .drawer__heading, [data-modal-title]");
    return (title?.textContent || "").trim().toLowerCase();
  };
  const isQuickAddPanel = (element) => {
    if (!element || element.nodeType !== 1 || isCartDrawerElement(element)) return false;
    if (element.matches?.(QUICK_ADD_SELECTOR)) return true;
    const title = ownTitleText(element);
    return title.includes("elegir opciones") ||
      title.includes("choose options") ||
      title.includes("seleccionar opciones") ||
      title.includes("select options");
  };
  const isPanelOpen = (element) => {
    if (!element || element.nodeType !== 1) return false;
    if (element.hasAttribute("open") || element.getAttribute("aria-hidden") === "false") return true;
    if (["active","is-open","open","modal--open","quick-add-modal--open"].some((name) => element.classList.contains(name))) return true;
    const styles = window.getComputedStyle(element);
    if (styles.display === "none" || styles.visibility === "hidden") return false;
    return element.getClientRects().length > 0;
  };
  const neutralizeQuickAddPanel = (element) => {
    if (!element || element.dataset.rpsNeutralizing === "true") return;
    element.dataset.rpsNeutralizing = "true";
    const previousDisplay = element.style.display;
    element.style.setProperty("display", "none", "important");
    try {
      if (typeof element.hide === "function") element.hide();
      else if (typeof element.close === "function") element.close();
    } catch (error) {}
    element.removeAttribute("open");
    element.removeAttribute("aria-modal");
    element.setAttribute("aria-hidden", "true");
    element.classList.remove("active","animate","is-open","open","modal--open","quick-add-modal--open");
    document.body.classList.remove("quick-add-modal-open","modal-open","product-modal-open","quick-buy-open");
    window.setTimeout(() => {
      if (previousDisplay) element.style.display = previousDisplay;
      else element.style.removeProperty("display");
      delete element.dataset.rpsNeutralizing;
    }, 700);
  };
  const neutralizeIfNeeded = (element) => {
    if (isQuickAddPanel(element) && isPanelOpen(element)) neutralizeQuickAddPanel(element);
  };
  const scanForQuickAdd = (root) => {
    const scope = root?.nodeType === 1 ? root : document.body;
    if (!scope) return;
    neutralizeIfNeeded(scope);
    scope.querySelectorAll?.(QUICK_ADD_SELECTOR).forEach(neutralizeIfNeeded);
  };
  let suppressorObserver = null;
  let suppressorTimer = null;
  const stopQuickAddSuppression = () => {
    suppressorObserver?.disconnect();
    suppressorObserver = null;
    window.clearTimeout(suppressorTimer);
    suppressorTimer = null;
  };
  const startQuickAddSuppression = (duration = 1800) => {
    stopQuickAddSuppression();
    scanForQuickAdd(document.body);
    suppressorObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes") {
          neutralizeIfNeeded(mutation.target);
          return;
        }
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) scanForQuickAdd(node);
        });
      });
    });
    suppressorObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["open","class","aria-hidden","style"]
    });
    suppressorTimer = window.setTimeout(stopQuickAddSuppression, duration);
  };
  const getCartDrawer = () =>
    document.querySelector("cart-drawer") ||
    document.querySelector("#mini-cart") ||
    document.querySelector("#CartDrawer") ||
    document.querySelector("[data-cart-drawer]") ||
    document.querySelector(".cart-drawer");
  const cleanEmptyDrawerClasses = (drawer) => {
    if (!drawer) return;
    drawer.classList.remove("is-empty","empty");
    drawer.querySelectorAll(".is-empty,.empty").forEach((element) => {
      element.classList.remove("is-empty","empty");
    });
  };
  const fetchCartJSON = async () => {
    const response = await fetch(`${normalizeRoot()}cart.js`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    });
    if (!response.ok) throw new Error("No se pudo leer cart.js");
    return response.json();
  };
  const fetchFreshCartSections = async () => {
    const response = await fetch(`${normalizeRoot()}cart?sections=${encodeURIComponent(CART_SECTION_IDS.join(","))}&_=${Date.now()}`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    });
    if (!response.ok) throw new Error("No se pudieron actualizar las secciones del carrito");
    return response.json();
  };
  const renderCartDrawerSection = (html) => {
    if (!html) return false;
    const parsed = parseHTML(html);
    const source = parsed.querySelector("cart-drawer,#mini-cart,#CartDrawer,.cart-drawer");
    const target = getCartDrawer();
    if (!source || !target) return false;
    target.innerHTML = source.innerHTML;
    cleanEmptyDrawerClasses(target);
    return true;
  };
  const renderCartCountSection = (html) => {
    if (!html) return false;
    const parsed = parseHTML(html);
    const sourceCounts = [...parsed.querySelectorAll("cart-count")];
    const targetCounts = [...document.querySelectorAll("cart-count")];
    if (!sourceCounts.length || !targetCounts.length) return false;
    targetCounts.forEach((target, index) => {
      target.textContent = (sourceCounts[index] || sourceCounts[0]).textContent;
    });
    return true;
  };
  const renderSectionsPayload = (sections) => {
    let drawerRendered = false;
    let bubbleRendered = false;
    if (!sections || typeof sections !== "object") return { drawerRendered, bubbleRendered };
    if (sections["mini-cart"]) drawerRendered = renderCartDrawerSection(sections["mini-cart"]);
    if (sections.header) bubbleRendered = renderCartCountSection(sections.header);
    return { drawerRendered, bubbleRendered };
  };
  const openCartDrawer = () => {
    scanForQuickAdd(document.body);
    const drawer = getCartDrawer();
    if (!drawer) return;
    cleanEmptyDrawerClasses(drawer);
    if (typeof drawer.open === "function") {
      drawer.open();
      return;
    }
    drawer.classList.add("animate","active","is-open");
    drawer.setAttribute("open","");
    drawer.removeAttribute("aria-hidden");
  };
  const addToCart = async (button) => {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.classList.contains("is-loading")) return;
    startQuickAddSuppression(2600);
    button.classList.add("is-loading");
    button.disabled = true;
    const formData = new FormData();
    formData.append("id", String(variantId));
    formData.append("quantity", "1");
    formData.append("sections", CART_SECTION_IDS.join(","));
    formData.append("sections_url", window.location.pathname);
    try {
      const response = await fetch(`${normalizeRoot()}cart/add.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || "No se pudo agregar el producto");
      let result = renderSectionsPayload(data.sections);
      if (!result.drawerRendered) {
        const sections = await fetchFreshCartSections();
        result = renderSectionsPayload(sections);
      }
      await fetchCartJSON().catch(() => null);
      openCartDrawer();
      button.classList.add("is-added");
      window.setTimeout(() => button.classList.remove("is-added"), 1200);
    } catch (error) {
      console.error("Rcrea Product Showcase:", error);
      button.classList.add("is-error");
      window.setTimeout(() => button.classList.remove("is-error"), 1200);
    } finally {
      button.classList.remove("is-loading");
      button.disabled = false;
    }
  };
  const closestAddButton = (target) => target?.closest?.(selectors.addButton);
  const triggerAddFromEvent = (event) => {
    const button = closestAddButton(event.target);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const now = Date.now();
    if (now - Number(button.dataset.rpsLastAdd || 0) < 900) return;
    button.dataset.rpsLastAdd = String(now);
    addToCart(button);
  };
  const blockThemeQuickAddEvent = (event) => {
    const button = closestAddButton(event.target);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    startQuickAddSuppression();
  };
  document.addEventListener("DOMContentLoaded", initCarousels);
  if (document.readyState !== "loading") initCarousels();
  ["pointerdown","mousedown","touchstart"].forEach((eventName) => {
    window.addEventListener(eventName, blockThemeQuickAddEvent, true);
  });
  ["pointerup","mouseup","touchend","click"].forEach((eventName) => {
    window.addEventListener(eventName, triggerAddFromEvent, true);
  });
  document.addEventListener("shopify:section:load", (event) => {
    const carousel = event.target.querySelector(selectors.carousel);
    if (!carousel || carousel.dataset.rpsCarouselInitialized === "true") return;
    carousel.dataset.rpsCarouselInitialized = "true";
    carousel.rcreaProductShowcaseCarousel = new RcreaProductShowcaseCarousel(carousel);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    const carousel = event.target.querySelector(selectors.carousel);
    if (!carousel?.rcreaProductShowcaseCarousel) return;
    carousel.rcreaProductShowcaseCarousel.destroy();
    delete carousel.rcreaProductShowcaseCarousel;
    delete carousel.dataset.rpsCarouselInitialized;
  });
})();